/* eslint-disable @typescript-eslint/no-explicit-any */
// routes/NavigationHandler.tsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import useAuth from "../hooks/useAuth";
import useHaptics from "../hooks/useHaptics";
import BottomNav from "../components/navbar/BottomNav";
import { tabRoutes, ROUTES_WITHOUT_NAV } from "./routes";
import { USER_MANUAL_PDF_URL } from "../utils/constants";
import SideNav from "../components/navbar/SideNav";
import {
  GUEST_ROUTE,
  exitGuestMode,
  isGuestSession,
} from "../utils/guestSession";
import { RequestPopup } from "../components/layout/Guest/Components/RequestPopup";
import { ImpactStyle } from "@capacitor/haptics";

interface NavigationHandlerProps {
  children: React.ReactNode;
}

// Routes where SideNav collapses so content gets the full width.
const FULL_SCREEN_ROUTES = ["/remote/curves"];

export const NavigationHandler: React.FC<NavigationHandlerProps> = ({
  children,
}) => {
  const { isAuthenticated, isDesktop } = useAuth();
  const history = useHistory();
  const location = useLocation();

  const [guestSession, setGuestSession] = useState(isGuestSession);
  // Session-scoped like auth, so the nav survives navigating to Remote.
  const isGuest = !isAuthenticated && guestSession;

  // Not route-aware on purpose — unmounting would remount children.
  const shouldShowSideNav = () =>
    isDesktop && (isAuthenticated || isGuest) && window.innerWidth >= 768;

  // Collapse instead of unmount, so it can animate.
  const sideNavCollapsed = FULL_SCREEN_ROUTES.includes(location.pathname);

  const [showBottomNav, setShowBottomNav] = useState(true);
  const [activeTab, setActiveTab] = useState("");
  const [showSideNav, setShowSideNav] = useState(() => shouldShowSideNav());
  const [showGuestGate, setShowGuestGate] = useState(false);
  const { impact, selectionFeedback, buttonTap } = useHaptics();
  const longPressTimer = useRef<any | null>(null);
  const isLongPressing = useRef(false);


  useEffect(() => {
    if (isAuthenticated || location.pathname.startsWith("/signin")) {
      exitGuestMode();
      setGuestSession(false);
      return;
    }
    setGuestSession(isGuestSession());
  }, [location.pathname, isAuthenticated]);

  // Sync showSideNav on window resize
  useEffect(() => {
    const handleResize = () => {
      setShowSideNav(shouldShowSideNav());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isDesktop, isAuthenticated, isGuest, location.pathname]);

  // Handle navigation state and active tab

  // Save current path to preferences
  useEffect(() => {
    if (location.pathname !== "/signin" && isAuthenticated) {
      Preferences.set({
        key: "prevPath",
        value: location.pathname,
      });
    }
    setShowSideNav(shouldShowSideNav());
  }, [location.pathname, isAuthenticated, isGuest, isDesktop]);

  useEffect(() => {
    // Redirect /ifu to the user manual PDF
    if (location.pathname === "/ifu") {
      window.location.href = USER_MANUAL_PDF_URL;
      return;
    }

    // Force full page load for /decrypt paths on browser only (handled by different application)
    if (location.pathname.includes("/decrypt") && !Capacitor.isNativePlatform()) {
      window.location.href = location.pathname + location.search;
      return;
    }

    const shouldShowNav = !ROUTES_WITHOUT_NAV.includes(location.pathname);
    setShowBottomNav(shouldShowNav);

    const activeRoute = tabRoutes.find((route) =>
      location.pathname.startsWith(route.path)
    );

    setActiveTab(activeRoute?.name || "");
  }, [location.pathname, location.search]);

  const handleLongPressStart = (onLongPress: () => void) => {
    if (isDesktop) return; // Disable haptics on desktop

    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }

    selectionFeedback();
    isLongPressing.current = false;

    longPressTimer.current = setTimeout(() => {
      impact(ImpactStyle.Heavy);
      isLongPressing.current = true;
      onLongPress();
    }, 800);
  };

  const handleLongPressEnd = () => {
    if (isDesktop) return; // Disable haptics on desktop

    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }

    if (isLongPressing.current) {
      selectionFeedback();
      isLongPressing.current = false;
    }
  };

  const handleTabLongPress = (tabName: string) => {
    switch (tabName) {
      case "home":
        history.replace("/home");
        break;
      case "activity":
        if (location.pathname !== "/activity") {
          history.push("/activity");
        }
        break;
      case "reflections":
        if (location.pathname !== "/journal") {
          history.push("/journal");
        }
        break;
      case "settings":
        history.push("/settings/account");
        break;
      default:
        break;
    }
  };
  const handleNavigate = useCallback(
    (tabName: string) => {
      // Both BottomNav and SideNav pass a tab name.
      const route = tabRoutes.find((r) => r.name === tabName);
      if (!route) return;

      const targetPath = route.path;
      // Live path off `history`, never a captured `location` - memoized
      // BottomNav can hold an older callback with a stale pathname.
      const currentPath = history.location.pathname;

      if (isGuest) {
        // Home means the guest page — return to it when elsewhere.
        if (route.name === "home") {
          if (currentPath !== GUEST_ROUTE) history.push(GUEST_ROUTE);
          setActiveTab("home");
          return;
        }
        // Open routes (Remote) work for guests as they do on mobile.
        if (!route.requiresAuth) {
          history.push(targetPath);
          setActiveTab(route.name);
          return;
        }
        setShowGuestGate(true);
        return;
      }

      if (currentPath === targetPath) return;

      // Set the active tab before navigating so the tap feels responsive;
      // otherwise activeTab desyncs from the URL and leaves the tab unresponsive.
      setActiveTab(tabName);
      buttonTap();

      const currentSection = `/${currentPath.split("/")[1]}`;
      const targetSection = `/${targetPath.split("/")[1]}`;

      if (
        currentSection === targetSection &&
        currentPath.startsWith(targetSection)
      ) {
        history.replace(targetPath);
      } else {
        history.push(targetPath);
      }
    },
    [history, location.pathname, isGuest, buttonTap]
  );

  const filteredTabs = tabRoutes.filter((route) => route.showInTabs);

  return (
    <>
      <div className="flex h-full w-full overflow-hidden">
        {showSideNav ? (
          <div className="flex flex-row h-full w-full">
            <div
              className={`h-full side-nav-container overflow-hidden${
                sideNavCollapsed ? " side-nav-container--collapsed" : ""
              }`}
            >
              <SideNav
                activeTab={activeTab}
                tabs={filteredTabs as any}
                onNavigate={handleNavigate}
                onLongPressStart={handleLongPressStart}
                onLongPressEnd={handleLongPressEnd}
                onTabLongPress={handleTabLongPress}
                collapsed={sideNavCollapsed}
              />
            </div>
            <div className="main-content w-full overflow-auto">{children}</div>
          </div>
        ) : (
          <div className="mobile-content w-full h-full overflow-auto">
            {children}
          </div>
        )}
      </div>
      {showBottomNav && !showSideNav && (
        <BottomNav
          activeTab={activeTab}
          tabs={tabRoutes}
          onNavigate={handleNavigate}
        />
      )}
      {showGuestGate && (
        <RequestPopup
          title="Guest View"
          buttonText="Proceed"
          onProceed={() => {
            setShowGuestGate(false);
            history.push("/signin?register=false");
          }}
          onClose={() => setShowGuestGate(false)}
        />
      )}
    </>
  );
};
